import BackButtonToolbar from "features/BackButtonToolbar";
import {
  ArrayInput,
  BooleanInput,
  FormDataConsumer,
  Labeled,
  ReferenceField,
  ReferenceInput,
  required,
  ResourceContextProvider,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  Title,
  useGetList,
  useNotify,
  useRedirect,
} from "react-admin";
import { useLocation, useParams } from "react-router-dom";
import dataProvider from "server/dataProvider";
import { ClassTypes, DisciplineCategory, StudentServicePath, WEEKLY_SCHEDULE } from "utils/constants";
import { Box, Grid } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { settings } from "app";
import { useNotifyError } from "utils/useNotifyError";
import { NationalityType, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { CircularProgress } from "@mui/material";
import { validateFromToWeek, validateTimeStart } from "../utils/utils";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import EditProfessors from "./EditProfessors";
import EditRooms from "./EditRooms";
import CourseInput from "./CourseInput";
import useConflictErrorModal from "server/components/Modals/useConflictErrorModal";
import WeekSelectInput from "../components/Inputs/WeekSelectInput";
import RoomInput from "../components/Inputs/RoomInput";
import { studentRequests } from "api/studentApi";
import { useEffect, useState } from "react";
import { tenants } from "utils/tenants";
import { useDisciplineCategory } from "../utils/useDisciplineCategory";

const EditWeeklySchedule = () => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const redirect = useRedirect();
  const { handleConflictError, ConflictErrorDialog } = useConflictErrorModal();

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const isEditProfessors = params.get("isEditProfessors");
  const isEditRooms = params.get("isEditRooms");
  const weeklyScheduleDisciplineId = params.get("id");
  const curriculumId = params.get("curriculumId");
  const specialityId = params.get("specialityId");

  const { weeklyScheduleId, disciplineId } = useParams();

  const [semesterAndWeek, setSemesterAndWeek] = useState(null);
  // The professors/rooms edit modes do not render the nationality input, so skip the request there.
  const disciplineCategory = useDisciplineCategory(disciplineId, { enabled: !isEditProfessors && !isEditRooms });

  const isForeignLanguage = disciplineCategory === DisciplineCategory.ForeignLanguage;

  useEffect(() => {
    if (!curriculumId) return;
    const fetchSemesterWeekNumber = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getCurriculumStudyWeeksSelect(curriculumId),
          null,
          "GET",
        );
        setSemesterAndWeek(data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchSemesterWeekNumber();
  }, [curriculumId]);

  const { data: editData, isLoading } = useGetList(
    studentRequests.getWeeklyScheduleDisciplineForUpdate(weeklyScheduleId, disciplineId),
    {},
    {
      enabled: !!weeklyScheduleId && !!disciplineId,
      onError: err => {
        notify(err.message || "Грешка при зареждане на данни", { type: "error" });
      },
    },
  );

  const currentSchedule = (editData || []).find(s => Number(s.id) === Number(weeklyScheduleDisciplineId));

  let defaultValues = {};

  if (isEditProfessors) {
    const target = (editData || []).find(s => Number(s.id) === Number(weeklyScheduleDisciplineId));
    defaultValues = {
      professors: (target?.professors || []).map(p => ({
        employeeId: p.employeeId,
        fromWeek: p.fromWeek,
        toWeek: p.toWeek,
      })),
    };
  } else if (isEditRooms) {
    const target = (editData || []).find(s => Number(s.id) === Number(weeklyScheduleDisciplineId));
    defaultValues = {
      roomId: target?.roomId,
      googleCourseRefId: target?.googleCourseRefId,
      isOnline: target?.isOnline,
    };
  } else {
    defaultValues = { schedules: editData || [] };
  }

  const submitFullSchedule = async values => {
    const body = {
      schedules: values.schedules.map(s => ({
        id: s.id,
        curriculumModuleId: s.curriculumModuleId,
        classTypeId: s.classTypeId,
        course: s.course,
        nationality: s.nationality ?? null,
        weekType: s.weekType,
        weekDay: s.weekDay,
        roomId: s.roomId,
        isOnline: s.isOnline,
        googleCourseRefId: s.googleCourseRefId,
        startTime: s.startTime,
        endTime: s.endTime,
        studyGroups: s.studyGroups,
        splitAcrossGroups: s.splitAcrossGroups,
        fromWeek: s.fromWeek,
        toWeek: s.toWeek,
        professors: (s.professors || []).map(p => ({
          employeeId: p.employeeId,
          fromWeek: p.fromWeek,
          toWeek: p.toWeek,
        })),
      })),
    };
    return dataProvider.customRequest(
      studentRequests.updateWeeklyScheduleDiscipline(weeklyScheduleId, disciplineId),
      body,
      "PUT",
    );
  };

  const submitProfessors = async values => {
    const professorsArr = (values.professors || []).map(p => ({
      employeeId: Number(p.employeeId),
      fromWeek: Number(p.fromWeek),
      toWeek: Number(p.toWeek),
    }));
    const body = { professors: professorsArr };

    await dataProvider.customRequest(
      studentRequests.updateWeeklyScheduleDisciplineProfessors(weeklyScheduleDisciplineId),
      body,
      "PUT",
    );
  };

  const submitRooms = async values => {
    const body = {
      roomId: values.roomId,
      isOnline: values.isOnline,
      googleCourseRefId: values.googleCourseRefId,
    };
    await dataProvider.customRequest(
      studentRequests.updateWeeklyScheduleDisciplineRooms(weeklyScheduleDisciplineId),
      body,
      "PUT",
    );
  };

  const handleSubmit = async values => {
    try {
      if (isEditProfessors) {
        await submitProfessors(values);
        notify("Преподавателите бяха успешно обновени", { type: "success" });
      } else if (isEditRooms) {
        await submitRooms(values);
        notify("Залата беше успешно обновена", { type: "success" });
      } else {
        await submitFullSchedule(values);
        notify("Разписанието беше успешно обновено", { type: "success" });
      }
      redirect(-1);
    } catch (err) {
      notifyError(err);
      handleConflictError(err);
    }
  };

  return (
    <>
      <Title
        title={
          isEditProfessors
            ? "Редактиране на седмично разписание (Преподавател)"
            : isEditRooms
              ? "Редактиране на седмично разписание (Зала)"
              : "Редактиране на седмично разписание"
        }
      />
      <BackButtonToolbar />
      <ResourceContextProvider value={`${StudentServicePath}${WEEKLY_SCHEDULE}`}>
        {isLoading ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={200} />
          </Box>
        ) : (
          <>
            <SimpleForm
              onSubmit={handleSubmit}
              defaultValues={defaultValues}
            >
              {isEditProfessors && (
                <EditProfessors
                  isLoading={isLoading}
                  defaultValues={defaultValues}
                  currentSchedule={currentSchedule}
                  disciplineId={disciplineId}
                  specialityId={specialityId}
                />
              )}

              {isEditRooms && (
                <EditRooms
                  isLoading={isLoading}
                  defaultValues={defaultValues}
                  currentSchedule={currentSchedule}
                  disciplineId={disciplineId}
                  specialityId={specialityId}
                />
              )}

              {!isEditProfessors && !isEditRooms && (
                <>
                  <Grid
                    container
                    sx={{ gap: 3, mb: 2 }}
                  >
                    <Grid
                      item
                      xs={12}
                      md={2}
                    >
                      <Labeled label="Специалност">
                        <ReferenceField
                          record={{ specialityId }}
                          source="specialityId"
                          reference={administrationRequests.getSpecialitiesSelect()}
                        >
                          <TextField source="name" />
                        </ReferenceField>
                      </Labeled>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={2}
                    >
                      <Labeled label="Дисциплина">
                        <ReferenceField
                          record={{ disciplineId }}
                          source="disciplineId"
                          reference={administrationRequests.getDisciplinesSelect()}
                        >
                          <TextField source="name" />
                        </ReferenceField>
                      </Labeled>
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      md={12}
                    >
                      <ArrayInput
                        source="schedules"
                        label="Разписания"
                        defaultValue={editData}
                      >
                        <SimpleFormIterator
                          disableReordering
                          disableClear
                          sx={settings.style.iteratorWithoutGap}
                        >
                          <FormDataConsumer>
                            {({ scopedFormData }) => {
                              return (
                                <>
                                  <Grid
                                    container
                                    spacing={2}
                                  >
                                    {isForeignLanguage && (
                                      <Grid
                                        item
                                        xs={12}
                                        md={3}
                                      >
                                        <SelectInput
                                          source="nationality"
                                          label="Националност"
                                          choices={NationalityType}
                                          validate={[required()]}
                                          fullWidth
                                        />
                                      </Grid>
                                    )}
                                    <Grid
                                      item
                                      xs={12}
                                      md={3}
                                    >
                                      <ReferenceInput
                                        source="classTypeId"
                                        reference={administrationRequests.getRoomClassTypesSelect()}
                                      >
                                        <SelectInput
                                          optionText="name"
                                          label="Тип занятия"
                                          validate={[required()]}
                                        />
                                      </ReferenceInput>
                                    </Grid>
                                    {disciplineId && curriculumId && (
                                      <Grid
                                        item
                                        xs={12}
                                        md={3}
                                      >
                                        <CourseInput
                                          curriculumId={curriculumId}
                                          disciplineId={disciplineId}
                                          defaultValues={defaultValues}
                                        />
                                      </Grid>
                                    )}
                                    <Grid
                                      item
                                      xs={12}
                                      md={3}
                                    >
                                      <WeekSelectInput
                                        semesterAndWeek={semesterAndWeek}
                                        contextMode="edit"
                                        scopedFormData={scopedFormData}
                                        source="fromWeek"
                                        label="От седмица"
                                        validate={[required(), () => validateFromToWeek(scopedFormData)]}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      md={3}
                                    >
                                      <WeekSelectInput
                                        semesterAndWeek={semesterAndWeek}
                                        contextMode="edit"
                                        scopedFormData={scopedFormData}
                                        source="toWeek"
                                        label="До седмица"
                                        validate={[required(), () => validateFromToWeek(scopedFormData)]}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      md={3}
                                    >
                                      <SelectInput
                                        source="weekType"
                                        label="Тип седмица"
                                        choices={WeekTypeChoices}
                                        validate={[required()]}
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      md={3}
                                    >
                                      <SelectInput
                                        source="weekDay"
                                        label="Ден от седмицата"
                                        choices={WeekDaysChoices}
                                        validate={[required()]}
                                        fullWidth
                                      />
                                    </Grid>
                                  </Grid>
                                  <RoomInput
                                    classTypeId={scopedFormData?.classTypeId}
                                    disciplineId={disciplineId}
                                  />
                                  <Grid
                                    container
                                    spacing={2}
                                  >
                                    <Grid
                                      item
                                      xs={12}
                                      md={2}
                                    >
                                      <CustomTimeOnlyInput
                                        source="startTime"
                                        label="Начален час"
                                        validate={[required(), () => validateTimeStart(scopedFormData)]}
                                        required
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      md={2}
                                    >
                                      <CustomTimeOnlyInput
                                        source="endTime"
                                        label="Краен час"
                                        validate={[required(), () => validateTimeStart(scopedFormData)]}
                                        required
                                      />
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    container
                                    spacing={2}
                                    mb={2}
                                  >
                                    <Grid
                                      item
                                      xs={12}
                                      md={4}
                                    >
                                      <StudyGroupField
                                        label=" Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако разписанието се отнася за всички
        групи!"
                                      />
                                    </Grid>
                                    {curriculumId && disciplineId && (
                                      <Grid
                                        item
                                        xs={12}
                                        md={4}
                                      >
                                        <ReferenceInput
                                          source="curriculumModuleId"
                                          reference={studentRequests.getCurriculumDisciplineModulesSelect(
                                            curriculumId,
                                            disciplineId,
                                          )}
                                        >
                                          <SelectInput
                                            optionText="name"
                                            label="Модул"
                                          />
                                        </ReferenceInput>
                                      </Grid>
                                    )}
                                    {scopedFormData?.classTypeId === ClassTypes.Exercies &&
                                      settings.tenant === tenants.MU && (
                                        <Grid
                                          item
                                          xs={12}
                                          md={4}
                                        >
                                          <BooleanInput
                                            source="splitAcrossGroups"
                                            label="Раздели по групи"
                                          />
                                        </Grid>
                                      )}
                                  </Grid>
                                  <ArrayInput
                                    source="professors"
                                    label="Преподаватели"
                                  >
                                    <SimpleFormIterator
                                      disableReordering
                                      disableClear
                                      sx={settings.style.iteratorWithoutGap}
                                    >
                                      <Grid
                                        container
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xs={12}
                                          md={12}
                                        >
                                          <ProfessorInput
                                            source="employeeId"
                                            label="Преподавател"
                                            filters={{ disciplineId }}
                                            fullWidth
                                            validate={[required()]}
                                          />
                                        </Grid>
                                      </Grid>
                                      <FormDataConsumer>
                                        {({ scopedFormData }) => (
                                          <Grid
                                            container
                                            spacing={2}
                                          >
                                            <Grid
                                              item
                                              xs={12}
                                              md={4}
                                            >
                                              <WeekSelectInput
                                                semesterAndWeek={semesterAndWeek}
                                                contextMode="edit"
                                                isChild={true}
                                                defaultValue={scopedFormData?.fromWeek}
                                                source="fromWeek"
                                                label="От седмица"
                                                validate={[required(), () => validateFromToWeek(scopedFormData)]}
                                              />
                                            </Grid>

                                            <Grid
                                              item
                                              xs={12}
                                              md={4}
                                            >
                                              <WeekSelectInput
                                                semesterAndWeek={semesterAndWeek}
                                                contextMode="edit"
                                                isChild={true}
                                                defaultValue={scopedFormData?.toWeek}
                                                source="toWeek"
                                                label="До седмица"
                                                validate={[required(), () => validateFromToWeek(scopedFormData)]}
                                              />
                                            </Grid>
                                          </Grid>
                                        )}
                                      </FormDataConsumer>
                                    </SimpleFormIterator>
                                  </ArrayInput>
                                </>
                              );
                            }}
                          </FormDataConsumer>
                        </SimpleFormIterator>
                      </ArrayInput>
                    </Grid>
                  </Grid>
                </>
              )}
            </SimpleForm>
            <ConflictErrorDialog />
          </>
        )}
      </ResourceContextProvider>
    </>
  );
};

export default EditWeeklySchedule;
